import { useTranslation } from 'react-i18next'

import { performRentalPortalListingsMassAction } from '../../../../../api'
import { EMPTY_LIST } from '../../../../../constants/common'
import { QA_NAME_CONFIRMATION } from '../../../../../constants/qa'
import { SUBMENU_PARAM_ARCHIVE_VALUE } from '../../../../../constants/route'
import {
  useMassActions,
  useNotification,
  usePerformMassAction,
} from '../../../../../hooks'
import { getPermanentFilters } from '../../../../../utils/filter'
import { ConfirmationModal } from '../../../../Confirmation'
import {
  getListingsMassActionNotificationTitle,
  LISTING_ACTION_RESTORE_MASS,
  LISTING_MASS_ACTION_NAMES,
} from '../../../../RentalPortalListing'
import {
  getIsRentalPortalArchiveMassRestoreAllowed,
  getRentalPortalArchiveMassActionEligibleCount,
  getRentalPortalArchiveMassActionEligibleItems,
  getRentalPortalArchiveMassActionItems,
  getRentalPortalArchiveMassActions,
  getRentalPortalArchiveMassRestoreDescription,
} from '../../RentalPortalArchive.utils'

import RestoreIcon from '../../../../../icons/restore.svg'

export function useRentalPortalArchiveMassActions({
  excludeIds,
  items,
  pagination,
  selectAllPages,
  selected,
  selectedSize,
  setModal,
  onCleanSelection,
  onClose,
  onReload,
}) {
  const { t } = useTranslation()

  const { handleAddErrorNotification } = useNotification()

  const {
    getAsyncTaskId,
    handleAddAsyncMassActionNotification,
    handleAddFinishMassActionNotification,
  } = useMassActions({
    getNotificationTitle: getListingsMassActionNotificationTitle,
    onSuccessHandler: onReload,
  })

  const massActionItems = getRentalPortalArchiveMassActionItems({
    excludeIds,
    items,
    selectAllPages,
    selected,
  })

  const eligibleItems =
    getRentalPortalArchiveMassActionEligibleItems(massActionItems)

  const eligibleCount = getRentalPortalArchiveMassActionEligibleCount({
    eligibleItems,
    massActionItems,
    selectedSize,
  })

  const massActions = getRentalPortalArchiveMassActions({
    eligibleCount,
    selectedSize,
    t,
  })

  function handleErrorAction() {
    handleAddErrorNotification('RentalPortal.ArchiveRestoreErrorKey')
  }

  const performerProps = {
    filterValues: getPermanentFilters({ sub: SUBMENU_PARAM_ARCHIVE_VALUE }),
    getAsyncTaskId,
    massActionNames: LISTING_MASS_ACTION_NAMES,
    pagination,
    performApi: performRentalPortalListingsMassAction,
    onAddAsyncMassActionNotification: handleAddAsyncMassActionNotification,
    onAddFinishMassActionNotification: handleAddFinishMassActionNotification,
    onCleanSelection,
    onCloseModal: onClose,
    onErrorAction: handleErrorAction,
    onReload,
  }

  const { massAction: performSelectionMassAction } = usePerformMassAction({
    ...performerProps,
    excludeIds,
    selectAllPages,
    selectedIds: eligibleItems.map(item => item.id),
  })

  const { massAction: performRowMassAction } = usePerformMassAction({
    ...performerProps,
    excludeIds: EMPTY_LIST,
    selectAllPages: false,
    selectedIds: EMPTY_LIST,
  })

  async function massAction({ action, ids }) {
    return ids?.length > 0
      ? performRowMassAction({ action, ids })
      : performSelectionMassAction({ action })
  }

  async function handleSubmitMassRestore() {
    return massAction({ action: LISTING_ACTION_RESTORE_MASS })
  }

  function setMassAction(action) {
    if (getIsRentalPortalArchiveMassRestoreAllowed({ action, eligibleCount })) {
      setModal(
        <ConfirmationModal
          actionButtonsProps={{
            submitProps: { beforeIconProps: { Icon: RestoreIcon } },
            submitText: t('RentalPortal.ListingRestoreBtnKey'),
          }}
          description={getRentalPortalArchiveMassRestoreDescription({
            eligibleCount,
            eligibleItems,
            selectedCount: selectedSize,
            t,
          })}
          qaName={QA_NAME_CONFIRMATION}
          onCancel={onClose}
          onSubmit={handleSubmitMassRestore}
        />
      )
    }
  }

  return { massAction, massActions, setMassAction }
}
